import { useState, useEffect } from 'react'
import { TrashIcon } from '../components/icons/TrashIcon'
import { PencilIcon } from '../components/icons/PencilIcon'
import { FileIcon } from '../components/icons/FileIcon'
import { GlobeIcon } from '../components/icons/GlobeIcon'
import { HeartbeatIcon } from '../components/icons/HeartbeatIcon'
import { BookIcon } from '../components/icons/BookIcon'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch, ApiError } from '../lib/api'
import type { KnowledgeSource, KnowledgeSourceListResponse } from '../lib/types'
import { Spinner } from '../components/Spinner'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog'
import { Card } from '../components/Card'
import { Toast } from '../components/Toast'
import {
  KnowledgeSourceForm,
  PulseDot,
  type KnowledgeSourceFormValue,
  type UrlProbeState,
  type KbFieldErrors,
  getKbFormErrors,
} from '../components/KnowledgeSourceForm'
import { useEntityEditorRegistration } from '../hooks/useEntityEditorRegistration'
import { NotSubscribedCard } from './PreviewPage'

/* ─── Helpers ─── */

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

type EditorMode = 'idle' | 'create' | 'rename'

type EditorForm = KnowledgeSourceFormValue

const emptyEditorForm: EditorForm = { sourceType: 'document', displayName: '', agentSummary: '', url: '' }

/* ─── Props ─── */

export interface GlobalKnowledgeTabProps {
  onAdded: (kbId: string) => void
  onRemoved: (kbId: string) => void
}

/* ─── Preview card (MS-866 AC1/AC2) ───
   Rendered instead of the full config UI below while FEATURES.globalKnowledgeConfig
   is false. The legacy UI is retained (hide-not-delete) — see ConnectionsPreview
   in GlobalConnectionsTab.tsx for the precedent. */

export function GlobalKnowledgePreview() {
  const { t } = useTranslation()
  return (
    <NotSubscribedCard
      cardTitle={t('globalKnowledgeTab.preview.cardTitle')}
      description={t('globalKnowledgeTab.preview.description')}
      voteFeature="Global Knowledge Base"
    />
  )
}

/* ─── GlobalKnowledgeTab ─── */

export function GlobalKnowledgeTab({ onAdded, onRemoved }: GlobalKnowledgeTabProps) {
  const { t } = useTranslation()
  const apiFetch = useApiFetch()
  const queryClient = useQueryClient()
  const [editorMode, setEditorMode] = useState<EditorMode>('idle')
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [editorForm, setEditorForm] = useState<EditorForm>(emptyEditorForm)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [editorError, setEditorError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<KnowledgeSource | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [deleteInUseDeps, setDeleteInUseDeps] = useState<{ agentId: string; agentDisplayName: string }[] | null>(null)
  const [urlProbeState, setUrlProbeState] = useState<Record<string, UrlProbeState>>({})
  const [urlWarningToast, setUrlWarningToast] = useState<string | null>(null)
  const [formProbeState, setFormProbeState] = useState<UrlProbeState>('idle')
  const [kbFieldErrors, setKbFieldErrors] = useState<KbFieldErrors>({})
  const [renameOriginal, setRenameOriginal] = useState<{ displayName: string; agentSummary: string; url: string } | null>(null)
  const [pendingSwitchTarget, setPendingSwitchTarget] = useState<KnowledgeSource | 'create' | null>(null)

  // Dirty detection built from scratch (no existing tracking here previously) —
  // mirrors KnowledgeTab.tsx's isAddDraftDirty/isRenameDraftDirty pattern:
  // create mode is dirty as soon as any field/file is populated; rename mode is
  // dirty relative to the snapshot taken when the rename editor was opened.
  const isAddDraftDirty = editorMode === 'create' && (editorForm.displayName !== '' || editorForm.agentSummary !== '' || editorForm.url !== '' || selectedFile !== null)
  const isRenameDraftDirty = editorMode === 'rename' && renameOriginal !== null &&
    (editorForm.displayName !== renameOriginal.displayName ||
      editorForm.agentSummary !== renameOriginal.agentSummary ||
      editorForm.url !== renameOriginal.url)
  const isDraftDirty = isAddDraftDirty || isRenameDraftDirty

  const sourcesQuery = useQuery({
    queryKey: ['knowledge-sources', 'global'],
    queryFn: () => apiFetch<KnowledgeSourceListResponse>('/api/v1/knowledge-sources?scope=Global'),
    refetchInterval: (query) => {
      const hasSyncing = (query.state.data?.items ?? []).some((s) => s.isSyncing)
      return hasSyncing ? 10_000 : false
    },
  })

  const sources = sourcesQuery.data?.items ?? []

  const createMutation = useMutation({
    mutationFn: async ({ file, form }: { file: File | null; form: EditorForm }) => {
      if (form.sourceType === 'document' && file) {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('metadata', JSON.stringify({ sourceType: 'Document', scope: 'Global', displayName: form.displayName || file.name, agentSummary: form.agentSummary }))
        return apiFetch<KnowledgeSource>('/api/v1/knowledge-sources', { method: 'POST', body: fd })
      } else {
        const payload = { scope: 'Global', sourceType: 'Web', url: form.url.trim(), displayName: form.displayName || form.url.trim(), agentSummary: form.agentSummary }
        return apiFetch<KnowledgeSource>('/api/v1/knowledge-sources', { method: 'POST', body: JSON.stringify(payload) })
      }
    },
    onSuccess: (source) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
      onAdded(source.id)
      resetEditor()
      if (source.sourceType === 'Web' && source.urlReachable === false) {
        setUrlWarningToast(t('globalKnowledgeTab.urlUnreachableWarning'))
      }
      if (source.sourceType === 'Web') {
        setUrlProbeState(prev => ({
          ...prev,
          [source.id]: source.urlReachable === true ? 'healthy' : source.urlReachable === false ? 'unreachable' : 'idle',
        }))
      }
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('globalKnowledgeTab.errors.failedToAdd'))
    },
  })

  const probeMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${sourceId}/probe-url`, { method: 'POST' }),
    onMutate: (sourceId: string) => {
      setUrlProbeState(prev => ({ ...prev, [sourceId]: 'checking' }))
    },
    onSuccess: (source) => {
      setUrlProbeState(prev => ({
        ...prev,
        [source.id]: source.urlReachable === true ? 'healthy' : 'unreachable',
      }))
    },
    onError: (_err: unknown, sourceId: string) => {
      setUrlProbeState(prev => ({ ...prev, [sourceId]: 'idle' }))
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, displayName, agentSummary, url }: { id: string; displayName: string; agentSummary: string; url?: string }) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ displayName, agentSummary, ...(url !== undefined ? { url } : {}) }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
      resetEditor()
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('globalKnowledgeTab.errors.failedToRename'))
    },
  })

  const reingestMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<KnowledgeSource>(`/api/v1/knowledge-sources/${id}/reingest`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
    },
    onError: (err: unknown) => {
      setEditorError(err instanceof Error ? err.message : t('globalKnowledgeTab.errors.failedToReingest'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      apiFetch<void>(`/api/v1/knowledge-sources/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-sources', 'global'] })
      onRemoved(id)
      if (selectedSourceId === id) resetEditor()
      setDeleteConfirm(null)
      setDeleteError(null)
      setDeleteInUseDeps(null)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409 && err.dependents?.length) {
        setDeleteInUseDeps(err.dependents as { agentId: string; agentDisplayName: string }[])
      } else {
        setDeleteError(err instanceof Error ? err.message : t('globalKnowledgeTab.errors.failedToDelete'))
        setDeleteConfirm(null)
      }
    },
  })

  useEffect(() => {
    const url = editorForm.url
    if (editorForm.sourceType !== 'web' || !url.trim() || (!url.startsWith('http://') && !url.startsWith('https://'))) {
      setFormProbeState('idle')
      return
    }
    const timer = setTimeout(async () => {
      setFormProbeState('checking')
      try {
        const result = await apiFetch<{ reachable: boolean }>('/api/v1/knowledge-sources/probe-url', {
          method: 'POST',
          body: JSON.stringify({ url }),
        })
        setFormProbeState(result.reachable ? 'healthy' : 'unreachable')
      } catch {
        setFormProbeState('idle')
      }
    }, 600)
    return () => clearTimeout(timer)
  }, [editorForm.url, editorForm.sourceType, apiFetch])

  function resetEditor() {
    setEditorMode('idle')
    setSelectedSourceId(null)
    setEditorForm(emptyEditorForm)
    setSelectedFile(null)
    setFileError(null)
    setEditorError(null)
    setFormProbeState('idle')
    setKbFieldErrors({})
    setRenameOriginal(null)
  }

  // Entity-editor registry (Knowledge/Global) — closing via resetEditor also
  // clears the rename snapshot above, matching markClean()'s contract.
  useEntityEditorRegistration({
    isOpen: () => editorMode !== 'idle',
    isDirty: () => isDraftDirty,
    markClean: () => resetEditor(),
  })

  function handleAdd() {
    setSelectedSourceId(null)
    setEditorMode('create')
    setEditorForm(emptyEditorForm)
    setSelectedFile(null)
    setFileError(null)
    setEditorError(null)
    setKbFieldErrors({})
  }

  function handleRename(source: KnowledgeSource) {
    setSelectedSourceId(source.id)
    setEditorMode('rename')
    const form: EditorForm = {
      sourceType: source.sourceType === 'Document' ? 'document' : 'web',
      displayName: source.displayName,
      // Legacy NULL source seeds empty — Save is blocked until typed (Decision 7).
      agentSummary: source.agentSummary ?? '',
      url: source.url ? String(source.url) : '',
    }
    setEditorForm(form)
    setRenameOriginal({ displayName: form.displayName, agentSummary: form.agentSummary, url: form.url })
    setEditorError(null)
    setKbFieldErrors({})
    setFormProbeState('idle')
  }

  /** Guards switching to add-new or a different source's rename form while the current draft is dirty. */
  function requestSwitch(target: KnowledgeSource | 'create') {
    if (isDraftDirty) {
      setPendingSwitchTarget(target)
    } else if (target === 'create') {
      handleAdd()
    } else {
      handleRename(target)
    }
  }

  function handleFileChange(file: File, err: string | null) {
    setFileError(err)
    if (!err) {
      setSelectedFile(file)
      setKbFieldErrors(e => { const next = { ...e }; delete next.file; return next })
      if (!editorForm.displayName) {
        setEditorForm(f => ({ ...f, displayName: file.name.replace(/\.[^.]+$/, '') }))
      }
    }
  }

  function handleSave() {
    if (editorMode === 'create') {
      const errs = getKbFormErrors(editorForm, selectedFile)
      if (Object.keys(errs).length > 0) {
        setKbFieldErrors(errs)
        if (errs.displayName) document.getElementById('gkb-name')?.focus()
        else if (errs.agentSummary) document.getElementById('gkb-agentsummary')?.focus()
        else if (errs.file) (document.querySelector('.upload-zone') as HTMLElement | null)?.focus()
        else if (errs.url) document.getElementById('gkb-url')?.focus()
        return
      }
      setKbFieldErrors({})
      createMutation.mutate({ file: selectedFile, form: editorForm })
    } else if (editorMode === 'rename' && selectedSourceId) {
      const errs = getKbFormErrors(editorForm, null, { requireFile: false })
      if (Object.keys(errs).length > 0) {
        setKbFieldErrors(errs)
        return
      }
      setKbFieldErrors({})
      renameMutation.mutate({
        id: selectedSourceId,
        displayName: editorForm.displayName.trim(),
        agentSummary: editorForm.agentSummary.trim(),
        ...(editorForm.sourceType === 'web' ? { url: editorForm.url.trim() } : {}),
      })
    }
  }

  const isSaving = createMutation.isPending || renameMutation.isPending

  return (
    <Card title={t('globalKnowledgeTab.title')} subtitle={t('globalKnowledgeTab.subtitle')} className="editor-split-card" noPadding>
      <div className="editor-split-body">
        {/* Left pane: source list */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
          {sourcesQuery.isLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}><Spinner /></div>}
          {sourcesQuery.isError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{t('globalKnowledgeTab.errors.failedToLoad')}</div>}
          {!sourcesQuery.isLoading && sources.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {t('globalKnowledgeTab.noSources')}
            </div>
          )}
          {sources.map(source => {
            const isDoc = source.sourceType === 'Document'
            const isSyncing = source.isSyncing
            const isFailed =
              !source.isIngested && !source.isSyncing && source.ingestionLastError !== null
            const isReingesting =
              reingestMutation.isPending && reingestMutation.variables === source.id
            const isProbing = probeMutation.isPending && probeMutation.variables === source.id
            const storedProbeState: UrlProbeState =
              source.urlReachable === true ? 'healthy' : source.urlReachable === false ? 'unreachable' : 'idle'
            const probeState = urlProbeState[source.id] ?? storedProbeState

            return (
              <div key={source.id}>
                <div className="file-row">
                  <div
                    className="file-icon-box"
                    style={isDoc ? undefined : { background: 'var(--info-bg)' }}
                  >
                    {isDoc ? <FileIcon /> : <GlobeIcon />}
                  </div>
                  <div className="file-info">
                    <div className="file-name">{source.displayName}</div>
                    <div className="file-meta">
                      {isDoc && source.fileSize != null
                        ? `${formatBytes(source.fileSize)} · ${formatDate(source.creationDate)}`
                        : source.url ?? formatDate(source.creationDate)}
                    </div>
                  </div>

                  {/* Ingestion status badge */}
                  {isSyncing && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--warning-bg)', color: 'var(--warning)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t('knowledgeTab.status.syncing')}
                    </span>
                  )}
                  {isFailed && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--danger-bg)', color: 'var(--danger)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t('knowledgeTab.status.failed')}
                    </span>
                  )}

                  {/* Retry button for failed Document sources */}
                  {isFailed && isDoc && (
                    <button
                      type="button"
                      onClick={() => reingestMutation.mutate(source.id)}
                      disabled={isReingesting}
                      className="btn btn-ghost" style={{ fontSize: 11, color: 'var(--warning)' }}
                      title={t('common.retry')}
                    >
                      {isReingesting ? <Spinner size={12} /> : t('common.retry')}
                    </button>
                  )}

                  {/* Web source health dot + probe button */}
                  {!isDoc && (
                    <>
                      <PulseDot state={isProbing ? 'checking' : probeState} />
                      <button
                        type="button"
                        onClick={() => probeMutation.mutate(source.id)}
                        disabled={isProbing}
                        className="skill-edit-btn"
                        title={t('globalKnowledgeTab.probeUrl')}
                        aria-label={t('globalKnowledgeTab.probeUrl')}
                      >
                        <HeartbeatIcon />
                      </button>
                    </>
                  )}

                  <button type="button" aria-label={t('globalKnowledgeTab.renameAria', { name: source.displayName })} onClick={() => requestSwitch(source)} className="skill-edit-btn"><PencilIcon size={14} /></button>
                  <button type="button" aria-label={t('globalKnowledgeTab.deleteAria', { name: source.displayName })} onClick={() => setDeleteConfirm(source)} className="icon-btn danger"><TrashIcon size={14} /></button>
                </div>

                {/* Ingestion error detail */}
                {isFailed && source.ingestionLastError && (
                  <div
                    style={{ padding: '4px 16px 6px', fontSize: 11, color: 'var(--danger)', background: 'var(--danger-bg)', borderBottom: '1px solid var(--border-light)' }}
                    title={source.ingestionLastError}
                  >
                    {source.ingestionLastError.slice(0, 120)}
                  </div>
                )}
              </div>
            )
          })}
          {deleteError && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)' }}>{deleteError}</div>}
        </div>

        {/* Separator */}
        <div style={{ width: 1, background: 'var(--border-light)', margin: '20px 0' }} />

        {/* Right pane: editor */}
        <div className="editor-pane-col">
          {editorMode === 'idle' && (
            <div className="editor-pane-empty">
              <BookIcon />
              <p style={{ fontSize: 13 }}>{t('globalKnowledgeTab.idle')}</p>
              <button type="button" onClick={() => requestSwitch('create')} className="btn btn-primary" style={{ fontSize: 13, padding: '5px 12px' }}>{t('globalKnowledgeTab.addNew')}</button>
            </div>
          )}

          {editorMode === 'rename' && (
            <div className="editor-form-wrap">
              <div className="editor-form-scroll">
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{t('globalKnowledgeTab.form.renameTitle')}</div>
                {editorError && <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{editorError}</div>}
                <KnowledgeSourceForm
                  mode="rename"
                  idPrefix="gkb-rename"
                  value={editorForm}
                  onChange={(patch) => {
                    if ('displayName' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.displayName; return next })
                    if ('agentSummary' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.agentSummary; return next })
                    if ('url' in patch) setFormProbeState('idle')
                    setEditorForm(f => ({ ...f, ...patch }))
                  }}
                  file={null}
                  onFileChange={() => { /* no-op — rename has no file re-upload */ }}
                  probeState={formProbeState}
                  fieldErrors={kbFieldErrors}
                />
              </div>
              <div className="editor-form-bar">
                <button type="button" onClick={resetEditor} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>{t('globalKnowledgeTab.form.cancel')}</button>
                <button type="button" onClick={handleSave} disabled={isSaving || Object.keys(kbFieldErrors).length > 0} className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px', opacity: (isSaving || Object.keys(kbFieldErrors).length > 0) ? 0.6 : 1 }}>
                  {isSaving ? <Spinner size={12} /> : null}
                  {t('globalKnowledgeTab.form.save')}
                </button>
              </div>
            </div>
          )}

          {editorMode === 'create' && (
            <div className="editor-form-wrap">
              <div className="editor-form-scroll">
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>{t('globalKnowledgeTab.form.addTitle')}</div>
                {editorError && <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{editorError}</div>}

                <KnowledgeSourceForm
                  idPrefix="gkb"
                  value={editorForm}
                  onChange={(patch) => {
                    if ('sourceType' in patch) { setSelectedFile(null); setFileError(null); setKbFieldErrors({}) }
                    if ('displayName' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.displayName; return next })
                    if ('url' in patch) setKbFieldErrors(e => { const next = { ...e }; delete next.url; return next })
                    setEditorForm(f => ({ ...f, ...patch }))
                  }}
                  file={selectedFile}
                  fileError={fileError}
                  onFileChange={handleFileChange}
                  probeState={formProbeState}
                  fieldErrors={kbFieldErrors}
                />
              </div>
              <div className="editor-form-bar">
                <button type="button" onClick={resetEditor} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>{t('globalKnowledgeTab.form.cancel')}</button>
                <button type="button" onClick={handleSave} disabled={isSaving || !!fileError || Object.keys(kbFieldErrors).length > 0} className="btn btn-primary" style={{ fontSize: 12, padding: '5px 12px', opacity: (isSaving || !!fileError || Object.keys(kbFieldErrors).length > 0) ? 0.6 : 1 }}>
                  {isSaving ? <Spinner size={12} /> : null}
                  {t('globalKnowledgeTab.form.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {deleteConfirm && !deleteInUseDeps && (
        <ConfirmDialog
          title={t('globalKnowledgeTab.deleteDialog.title', { name: deleteConfirm.displayName })}
          message={t('globalKnowledgeTab.deleteDialog.message')}
          confirmLabel={t('globalKnowledgeTab.deleteDialog.confirm')}
          danger
          onConfirm={() => deleteMutation.mutate({ id: deleteConfirm.id })}
          onCancel={() => { setDeleteConfirm(null); setDeleteError(null) }}
        />
      )}
      {deleteConfirm && deleteInUseDeps && (
        <ConfirmDialog
          title={t('globalKnowledgeTab.deleteInUseDialog.title')}
          message={t('globalKnowledgeTab.deleteInUseDialog.message')}
          body={
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                {t('globalKnowledgeTab.deleteInUseDialog.affectedLabel')}
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {deleteInUseDeps.map((d) => (
                  <li key={d.agentId}>{d.agentDisplayName}</li>
                ))}
              </ul>
            </div>
          }
          confirmLabel={t('globalKnowledgeTab.deleteInUseDialog.confirm')}
          requiresAcknowledgment
          acknowledgmentLabel={t('globalKnowledgeTab.deleteInUseDialog.acknowledgment')}
          danger
          onConfirm={() => deleteMutation.mutate({ id: deleteConfirm.id, force: true })}
          onCancel={() => { setDeleteConfirm(null); setDeleteInUseDeps(null); setDeleteError(null) }}
        />
      )}

      {urlWarningToast && (
        <Toast
          message={urlWarningToast}
          variant="warning"
          onDismiss={() => setUrlWarningToast(null)}
        />
      )}

      {pendingSwitchTarget && (
        <UnsavedChangesDialog
          onConfirm={() => {
            const target = pendingSwitchTarget
            setPendingSwitchTarget(null)
            if (target === 'create') {
              handleAdd()
            } else {
              handleRename(target)
            }
          }}
          onCancel={() => setPendingSwitchTarget(null)}
        />
      )}
    </Card>
  )
}
